import type {
  NewPathway,
  Pathway,
  PathwayDraft,
  PathwayEvaluation,
  PathwayLight,
} from '@/types';
import { api } from './api';

// The new Pathway-on-ledger resource. Reads carry the `Pathways` tag so a
// save/create/delete refetches the list and the open detail; the simulate
// query is POST-with-draft — the draft is part of its cache key, so every
// edit of the effective pathway re-runs the evaluation live (Save persists
// separately). Mirrors the disclosures api; the legacy surface stays in
// `pathway-scenarios.ts`.
const pathwaysApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPathways: builder.query<PathwayLight[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/pathways`,
      providesTags: ['Pathways'],
    }),
    getPathway: builder.query<Pathway, number>({
      query: (id) => `pathways/${id}`,
      providesTags: ['Pathways'],
    }),
    newPathway: builder.mutation<Pathway, NewPathway>({
      query: ({ inventoryId, name }) => ({
        url: `inventories/${inventoryId}/pathways`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Pathways'],
    }),
    savePathway: builder.mutation<Pathway, { id: number } & PathwayDraft>({
      query: ({ id, ...draft }) => ({
        url: `pathways/${id}`,
        method: 'POST',
        body: draft,
      }),
      invalidatesTags: ['Pathways'],
    }),
    deletePathway: builder.mutation<{}, { pathwayId: number }>({
      query: ({ pathwayId }) => ({
        url: `pathways/${pathwayId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Pathways'],
    }),
    pathwaySimulation: builder.query<
      PathwayEvaluation,
      { pathwayId: number; draft: PathwayDraft }
    >({
      query: ({ pathwayId, draft }) => ({
        url: `pathways/${pathwayId}/simulation`,
        method: 'POST',
        body: draft,
      }),
    }),
    // The evaluation's CSVs, zipped. Exports the *saved* pathway (an empty
    // body — the suit toolbar-export idiom); the signed download URL the
    // server returns is followed in `onQueryStarted`, so the caller just
    // fires it. The locked transfer-download pattern.
    downloadPathway: builder.mutation<void, { pathwayId: number }>({
      query: ({ pathwayId }) => ({
        url: `pathways/${pathwayId}/download`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        queryFulfilled.then(({ data }: any) => {
          window.location = data.url;
        });
      },
    }),
  }),
});

export const {
  useGetPathwaysQuery,
  useGetPathwayQuery,
  useNewPathwayMutation,
  useSavePathwayMutation,
  useDeletePathwayMutation,
  usePathwaySimulationQuery,
  useDownloadPathwayMutation,
} = pathwaysApi;
